/*
 * The continue-onboarding footer that every outbound sales message carries when
 * the task has an onboarding link.
 *
 * Idempotence is the whole point. The same draft can pass through here more than
 * once — a suggestion is copied into the textarea (footer appended), the agent
 * edits it and hits Send SMS (appended again), or Send chat and then the
 * WhatsApp hand-off from the same box. The server also appends this footer on
 * /v2/sales_task_list/send_sms, so a message composed here and sent there would
 * otherwise carry the link twice. Containment, not a suffix check, is what makes
 * all of those safe: the agent may have typed something after the link.
 *
 * The footer text is duplicated in goserver (the server-side append). It is one
 * product-owned sentence, agreed with Kuldeep; changing it means changing both.
 */

// Trailing space is part of the constant: the link is concatenated directly.
export const FOOTER = 'Just one step between you and your first free session - continue here: ';

/*
 * Append the footer to a composed message.
 *
 * Returns `text` untouched when there is no link, or when the link already
 * appears anywhere in the text. An empty draft gets the footer without the
 * leading blank lines, so a message never opens on whitespace.
 */
export const withOnboardingLink = (text: unknown, link: unknown): string => {
  const body = typeof text === 'string' ? text : '';
  const url = typeof link === 'string' ? link.trim() : '';
  if (!url) return body;
  if (body.includes(url)) return body;
  const footer = `${FOOTER}${url}`;
  if (!body.trim()) return footer;
  return `${body}\n\n${footer}`;
};

/*
 * The send paths' guard and footer in one step: an empty (or whitespace-only)
 * draft yields '', never the bare footer.
 *
 * It is one function rather than two lines at each call site because getting the
 * order wrong is silent: withOnboardingLink('') returns the footer alone, so
 * footering before the empty check turns "Send" on an empty box into an outbound
 * message containing nothing but a login link. Callers treat '' as "nothing to
 * send" and say so to the operator.
 */
export const compose_outbound_message = (text: unknown, link: unknown): string => {
  const draft = typeof text === 'string' ? text.trim() : '';
  if (!draft) return '';
  return withOnboardingLink(draft, link);
};
